import { useState } from "react";
import { Link } from "react-router-dom";

import { LabeebStandingControl } from "../../../components/labeeb/LabeebStandingControl";
import type { AppConfig } from "../../../config";
import { Button, Input } from "../../../f7";

/**
 * Look up one merchant and change its Labeeb standing.
 *
 * Labeeb serves no fleet-wide list of suspended businesses, so this takes a
 * reference rather than rendering a table. That is the honest shape: a table
 * assembled by fetching standing per merchant would be one request per row to
 * show a column that is empty for almost all of them.
 *
 * Deliberately the same control as the merchant page's — not a second
 * implementation. Two doors onto one write is the console's existing pattern
 * for MCP blocking, and the risk with two doors is that they drift, which
 * sharing the component removes.
 */
export function LabeebSuspendLookup({ config }: { config: AppConfig }) {
  const [typed, setTyped] = useState("");
  const [looked, setLooked] = useState("");

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setLooked(typed.trim());
        }}
      >
        <div className="w-64 max-w-full">
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Business reference, e.g. 507077"
            aria-label="Merchant to look up in Labeeb"
          />
        </div>
        <Button type="submit" disabled={typed.trim() === ""}>
          Look up
        </Button>
      </form>

      {looked === "" ? (
        <p className="text-xs/5 text-slate-500 dark:text-slate-400">
          Labeeb has no fleet-wide list of suspensions, so this works one merchant at a
          time. The same control is on each merchant&rsquo;s own page.
        </p>
      ) : (
        <div>
          <p className="mb-2 text-sm">
            <span className="ref">{looked}</span>{" "}
            <Link to={`/tenants/${encodeURIComponent(looked)}`} className="underline">
              open the merchant
            </Link>
          </p>
          <LabeebStandingControl config={config} tenantId={looked} />
        </div>
      )}
    </div>
  );
}
